import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import { useNotifications as useNotificationService } from './useNotifications';
import type { Chat, Message } from '../types/chat';
import type { RealtimeChannel } from '@supabase/supabase-js';

export function useChat(chatId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { sendChatMessage } = useNotificationService();

  // Fetch chat details
  const { data: chat, ...chatQuery } = useQuery({
    queryKey: ['chat', chatId],
    queryFn: async () => {
      try {
        // First fetch the chat
        const { data: chatData, error: chatError } = await supabase
          .from('chats')
          .select('*')
          .eq('id', chatId)
          .single();

        if (chatError) {
          console.error('Error fetching chat:', chatError);
          throw chatError;
        }

        if (!chatData) {
          throw new Error('Chat not found');
        }

        // Fetch vendor info
        const { data: vendorData, error: vendorError } = await supabase
          .from('vendors')
          .select('*')
          .eq('id', chatData.vendor_id)
          .single();

        if (vendorError && vendorError.code !== 'PGRST116') {
          console.error('Error fetching vendor:', vendorError);
          // Don't throw an error, just continue without vendor data
          console.warn('Continuing without vendor data');
        }

        // Fetch all participants
        const { data: participants, error: participantsError } = await supabase
          .from('chat_participants')
          .select('user_id')
          .eq('chat_id', chatId);

        if (participantsError) {
          console.error('Error fetching participants:', participantsError);
          throw participantsError;
        }

        // TEMPORARY FIX: Instead of throwing an error, create a placeholder participant
        // with the current user if no participants are found
        let participantsToUse = participants;
        if (!participants?.length) {
          console.warn(`No participants found for chat ${chatId}, adding current user as participant`);
          
          // Add the current user as a participant to avoid breaking the app
          participantsToUse = [{ user_id: user?.id }];
          
          // Try to create the missing participant record in the background
          if (user?.id) {
            supabase.from('chat_participants')
              .insert({ chat_id: chatId, user_id: user.id })
              .then(({ error }) => {
                if (error) {
                  console.error('Error creating missing participant:', error);
                } else {
                  console.log('Successfully created missing participant');
                }
              });
          }
        }

        // Fetch profiles for all participants
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url')
          .in('id', participantsToUse.map(p => p.user_id));

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }

        // Also fetch vendor information for all participants
        const { data: userVendors, error: userVendorsError } = await supabase
          .from('vendors')
          .select('id, store_name, user_id')
          .in('user_id', participantsToUse.map(p => p.user_id));
          
        if (userVendorsError) {
          console.error('Error fetching user vendors:', userVendorsError);
          // Don't throw, just continue without vendor data
        }
        
        // Create a map of user_id to vendor info
        const userVendorMap = new Map();
        if (userVendors) {
          userVendors.forEach(vendor => {
            userVendorMap.set(vendor.user_id, vendor);
          });
        }
        
        console.log('User vendor map:', Array.from(userVendorMap.entries()));

        // Map each participant to their profile
        const participantsWithProfiles = participantsToUse.map(p => {
          const userProfile = profiles.find(profile => profile.id === p.user_id);
          const userVendor = userVendorMap.get(p.user_id);
          
          return {
            user_id: p.user_id,
            user: userProfile ? {
              ...userProfile,
              vendor: userVendor || null
            } : undefined
          };
        });

        return {
          ...chatData,
          vendor: vendorData,
          participants: participantsWithProfiles
        } as Chat;
      } catch (error) {
        console.error('Error in chat query:', error);
        throw error;
      }
    },
    enabled: !!chatId && !!user,
  });

  // Fetch messages
  const { data: messages, ...messagesQuery } = useQuery({
    queryKey: ['chat-messages', chatId],
    queryFn: async () => {
      try {
        // Fetch messages
        const { data: messagesData, error: messagesError } = await supabase
          .from('messages')
          .select('*')
          .eq('chat_id', chatId)
          .order('created_at', { ascending: true });

        if (messagesError) {
          console.error('Error fetching messages:', messagesError);
          throw messagesError;
        }

        if (!messagesData?.length) {
          return [];
        }

        // Fetch profiles for message senders
        const senderIds = [...new Set(messagesData.map(m => m.sender_id))];
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url')
          .in('id', senderIds);

        if (profilesError) {
          console.error('Error fetching sender profiles:', profilesError);
          throw profilesError;
        }

        return messagesData.map(message => ({
          ...message,
          sender: profiles.find(p => p.id === message.sender_id)
        })) as Message[];
      } catch (error) {
        console.error('Error in messages query:', error);
        throw error;
      }
    },
    enabled: !!chatId && !!user,
  });

  // Ref to track the current channel for proper cleanup
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isSubscribedRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handler for processing new messages from realtime subscription
  const handleNewMessage = useCallback(async (payload: any) => {
    if (!user) return;
    
    console.log('💬 [REALTIME] Received new message:', {
      chatId,
      messageId: payload.new.id,
      senderId: payload.new.sender_id,
      text: payload.new.text?.substring(0, 50) + '...',
      isFromCurrentUser: payload.new.sender_id === user.id
    });

    try {
      // Get sender profile for the new message
      const { data: senderProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, username, avatar_url')
        .eq('id', payload.new.sender_id)
        .single();

      if (profileError) {
        console.error('💬 [REALTIME] Error fetching sender profile:', profileError);
      }

      // Create new message object with sender info (even if profile fetch failed)
      const newMessage = {
        ...payload.new,
        sender: senderProfile || { id: payload.new.sender_id }
      };

      // Update messages cache directly for immediate UI update
      queryClient.setQueryData(['chat-messages', chatId], (old: any[] = []) => {
        const oldMessages = Array.isArray(old) ? old : [];
        
        // Check if message already exists to prevent duplicates
        if (oldMessages.some(msg => msg.id === newMessage.id)) {
          console.log('💬 [REALTIME] Duplicate message detected, skipping:', newMessage.id);
          return oldMessages;
        }
        
        console.log('💬 [REALTIME] Adding new message to cache:', {
          messageId: newMessage.id,
          text: newMessage.text?.substring(0, 30) + '...',
          sender: newMessage.sender?.username || newMessage.sender?.full_name || 'Unknown',
          currentCount: oldMessages.length
        });
        
        const updatedMessages = [...oldMessages, newMessage].sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        
        console.log('💬 [REALTIME] Updated messages count:', updatedMessages.length);
        return updatedMessages;
      });

      // Update chat list with new last message for immediate update
      queryClient.setQueryData(['chats', user.id], (old: any[] = []) => {
        if (!Array.isArray(old)) return old;
        
        return old.map(chat => {
          if (chat.id === chatId) {
            return {
              ...chat,
              last_message: {
                id: newMessage.id,
                chat_id: chatId,
                text: newMessage.text,
                created_at: newMessage.created_at,
                sender_id: newMessage.sender_id,
                sender: senderProfile
              },
              updated_at: newMessage.created_at
            };
          }
          return chat;
        }).sort((a, b) => 
          new Date(b.updated_at || b.created_at).getTime() - 
          new Date(a.updated_at || a.created_at).getTime()
        );
      });

    } catch (error) {
      console.error('💬 [REALTIME] Error processing new message:', error);
      // Fallback to invalidating queries
      queryClient.invalidateQueries({ queryKey: ['chat-messages', chatId] });
      queryClient.invalidateQueries({ queryKey: ['chats', user.id] });
    }
  }, [chatId, queryClient, user]);

  // Subscribe to new messages for this specific chat with robust reconnection
  useEffect(() => {
    if (!chatId || !user) return;

    const setupSubscription = () => {
      // Clean up existing channel first
      if (channelRef.current) {
        console.log('💬 [REALTIME] Cleaning up existing channel for chat:', chatId);
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        isSubscribedRef.current = false;
      }

      console.log('💬 [REALTIME] Setting up real-time subscription for chat:', chatId);

      const channel = supabase
        .channel(`chat-messages-${chatId}-${Date.now()}`) // Unique channel name
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`,
        }, handleNewMessage)
        .subscribe((status, err) => {
          console.log('💬 [REALTIME] Subscription status for chat', chatId, ':', status);
          
          if (status === 'SUBSCRIBED') {
            console.log('💬 [REALTIME] ✅ Successfully subscribed to chat messages');
            isSubscribedRef.current = true;
          } else if (status === 'CHANNEL_ERROR') {
            console.error('💬 [REALTIME] ❌ Channel error:', err);
            isSubscribedRef.current = false;
            
            // Attempt to reconnect after a delay
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('💬 [REALTIME] Attempting to reconnect...');
              setupSubscription();
            }, 3000);
          } else if (status === 'TIMED_OUT') {
            console.error('💬 [REALTIME] ⏰ Subscription timed out');
            isSubscribedRef.current = false;
            
            // Attempt to reconnect
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('💬 [REALTIME] Attempting to reconnect after timeout...');
              setupSubscription();
            }, 2000);
          } else if (status === 'CLOSED') {
            console.log('💬 [REALTIME] Channel closed for chat:', chatId);
            isSubscribedRef.current = false;
          }
        });

      channelRef.current = channel;
    };

    // Initial subscription setup
    setupSubscription();

    // Cleanup function
    return () => {
      console.log('💬 [REALTIME] Cleaning up subscription for chat:', chatId);
      
      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Remove the channel
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        isSubscribedRef.current = false;
      }
    };
  }, [chatId, user, handleNewMessage]);

  // Send message mutation with optimistic updates
  const sendMessage = useMutation({
    mutationFn: async (text: string) => {
      // Generate a temporary ID for optimistic update
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const now = new Date().toISOString();
      
      // Create optimistic message to show immediately
      const optimisticMessage = {
        id: tempId,
        chat_id: chatId,
        sender_id: user?.id,
        text,
        created_at: now,
        sender: {
          id: user?.id,
          full_name: user?.user_metadata?.full_name || '',
          username: user?.user_metadata?.username || user?.email?.split('@')[0] || '',
          avatar_url: user?.user_metadata?.avatar_url || null
        },
        _isOptimistic: true // Flag to identify optimistic messages
      };

      // OPTIMISTIC UPDATE: Add message to cache immediately
      queryClient.setQueryData(['chat-messages', chatId], (old: Message[] = []) => {
        console.log('📤 [OPTIMISTIC] Adding optimistic message to cache:', tempId);
        return [...old, optimisticMessage].sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });

      try {
        // Actually insert the message in the database
        const { data: messagesData, error: messageError } = await supabase
          .from('messages')
          .insert({
            chat_id: chatId,
            sender_id: user?.id,
            text
          })
          .select('*');

        if (messageError) {
          console.error('Error sending message:', messageError);
          // Remove the optimistic message on error
          queryClient.setQueryData(['chat-messages', chatId], (old: Message[] = []) => {
            return old.filter(msg => msg.id !== tempId);
          });
          throw messageError;
        }

        if (!messagesData || messagesData.length === 0) {
          // Remove the optimistic message on error
          queryClient.setQueryData(['chat-messages', chatId], (old: Message[] = []) => {
            return old.filter(msg => msg.id !== tempId);
          });
          throw new Error('Failed to create message');
        }

        const messageData = messagesData[0];

        // Fetch sender profile using IN operator like in messages query
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url')
          .in('id', [messageData.sender_id]);

        if (profileError) {
          console.error('Error fetching sender profile:', profileError);
          throw profileError;
        }

        const senderProfile = profiles?.[0];
        if (!senderProfile) {
          console.error('Sender profile not found');
          throw new Error('Sender profile not found');
        }

        const newMessage = {
          ...messageData,
          sender: senderProfile
        };

        // Replace the optimistic message with the real one
        queryClient.setQueryData(['chat-messages', chatId], (old: Message[] = []) => {
          console.log('📤 [OPTIMISTIC] Replacing optimistic message with real message:', {
            tempId,
            realId: newMessage.id
          });
          // Remove optimistic message and add real message (if not already there via realtime)
          const withoutOptimistic = old.filter(msg => msg.id !== tempId);
          if (withoutOptimistic.some(msg => msg.id === newMessage.id)) {
            return withoutOptimistic;
          }
          return [...withoutOptimistic, newMessage].sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        });

        // Update chat list with the new message
        queryClient.setQueryData(['chats', user?.id], (old: Chat[] | undefined) => {
          if (!old || !Array.isArray(old)) return old;
          
          return old.map(c => {
            if (c.id === chatId) {
              return {
                ...c,
                last_message: {
                  id: newMessage.id,
                  chat_id: chatId,
                  text: newMessage.text,
                  created_at: newMessage.created_at,
                  sender_id: newMessage.sender_id,
                  sender: senderProfile
                },
                updated_at: newMessage.created_at
              };
            }
            return c;
          }).sort((a, b) => 
            new Date(b.updated_at || b.created_at).getTime() - 
            new Date(a.updated_at || a.created_at).getTime()
          );
        });

        // Send chat message notification to other participants
        if (chat?.participants?.length) {
          console.log('📤 [CHAT NOTIFICATION] Processing notification for chat participants:', {
            totalParticipants: chat.participants.length,
            currentUserId: user?.id,
            chatId,
          });

          const otherParticipants = chat.participants.filter(
            (participant) => participant.user_id && participant.user_id !== user?.id
          );

          console.log('📤 [CHAT NOTIFICATION] Other participants to notify:', {
            count: otherParticipants.length,
            participants: otherParticipants.map(p => ({
              userId: p.user_id,
              userName: p.user?.full_name || p.user?.username || 'Unknown',
            })),
          });

          for (const participant of otherParticipants) {
            const recipientId = participant.user_id;
            if (!recipientId) {
              console.warn('⚠️ [CHAT NOTIFICATION] Skipping participant without user_id', participant);
              continue;
            }

            try {
              console.log('📤 [CHAT NOTIFICATION] Sending to recipient:', {
                recipientId,
                recipientName: participant.user?.full_name || participant.user?.username || 'Unknown',
                senderName: senderProfile.full_name || senderProfile.username || 'Someone',
                messagePreview: text.substring(0, 30) + (text.length > 30 ? '...' : ''),
              });

              const result = await sendChatMessage(
                recipientId,
                chatId,
                senderProfile.full_name || senderProfile.username || 'Someone',
                text
              );

              console.log('✅ [CHAT NOTIFICATION] Result:', {
                recipientId,
                tokenCount: result?.token_count ?? 'N/A',
                userCount: result?.user_count ?? 'N/A',
                success: result?.push_results?.tickets?.filter(t => t.status === 'ok').length ?? 'N/A',
              });
            } catch (notifError) {
              console.error('❌ [CHAT NOTIFICATION] Failed to send:', {
                recipientId,
                error: notifError instanceof Error ? notifError.message : String(notifError),
              });
              // Don't fail the whole process if notification fails
            }
          }
        } else {
          console.log('⚠️ [CHAT NOTIFICATION] No participants to notify (chat?.participants is empty)');
        }

        return newMessage;
      } catch (error) {
        console.error('Error in send message mutation:', error);
        throw error;
      }
    }
  });

  return {
    chat,
    messages,
    isLoading: chatQuery.isLoading || messagesQuery.isLoading,
    error: chatQuery.error || messagesQuery.error,
    sendMessage: sendMessage.mutate,
  };
} 